import { animate } from "./animate";
import { debounce } from "./debounce";
import { ownerWindow } from "./owner-window";

export class MarkScrollableTabs {
  #element = null;
  #cleanup = [];

  #getTabsScroller() {
    return this.#element?.querySelector("[data-scroller]");
  }

  #getTabsList() {
    return this.#element?.querySelector("[data-tablist]");
  }

  #getScrollButtons() {
    /** @type {NodeListOf<HTMLButtonElement>} */
    const scrollButtons = this.#element?.querySelectorAll(
      "[data-scroll-button]"
    );
    if (!scrollButtons) {
      return [];
    }
    return Array.from(scrollButtons).filter((scrollButton) => {
      const scrollButtonType = scrollButton.dataset?.scrollButton;
      if (
        typeof scrollButtonType !== "string" ||
        ["increment", "decrement"].indexOf(scrollButtonType) === -1
      ) {
        return false;
      }
      return true;
    });
  }

  #getScrollbar() {
    return this.#element?.querySelector("[data-scrollbar-size]");
  }

  #getScrollSize() {
    if (!this.#element) {
      return 0;
    }

    const tabsScroller = this.#getTabsScroller();
    const tabsList = this.#getTabsList();
    if (!tabsScroller || !tabsList) {
      return 0;
    }
    const containerSize = tabsScroller["clientWidth"];
    let totalSize = 0;
    const children = Array.from(tabsList.children);

    for (let i = 0; i < children.length; i += 1) {
      const tab = children[i];
      if (totalSize + tab["clientWidth"] > containerSize) {
        // If the first item is longer than the container size, then only scroll
        // by the container size.
        if (i === 0) {
          totalSize = containerSize;
        }
        break;
      }
      totalSize += tab["clientWidth"];
    }

    return totalSize;
  }

  #scroll(scrollValue, { animation = true } = {}) {
    const tabsScroller = this.#getTabsScroller();
    if (!tabsScroller) {
      return;
    }
    if (animation) {
      animate("scrollLeft", tabsScroller, scrollValue, {
        duration: 300,
      });
    } else {
      tabsScroller["scrollLeft"] = scrollValue;
    }
  }

  #moveTabsScroll(delta) {
    const tabsScroller = this.#getTabsScroller();
    if (!tabsScroller) {
      return;
    }
    let scrollValue = tabsScroller["scrollLeft"];
    scrollValue += delta;
    this.#scroll(scrollValue);
  }

  #handleStartScrollClick() {
    this.#moveTabsScroll(-1 * this.#getScrollSize());
  }

  #handleEndScrollClick() {
    this.#moveTabsScroll(this.#getScrollSize());
  }

  #handleScrollbarSizeChange() {
    const tabsScroller = this.#getTabsScroller();
    if (!tabsScroller) {
      return;
    }
    tabsScroller.style.overflow = null;
    tabsScroller.style.marginBottom = 0;
  }

  #scrollbarSizeListener() {
    const scrollbar = this.#getScrollbar();
    if (!scrollbar) {
      return () => {};
    }
    let scrollbarHeight = 0;
    const setMeasurements = () => [
      (scrollbarHeight = scrollbar.offsetHeight - scrollbar.clientHeight),
    ];
    const handleResize = debounce(() => {
      const prevHeight = scrollbarHeight;
      setMeasurements();
      if (prevHeight !== scrollbarHeight) {
        this.#handleScrollbarSizeChange(scrollbarHeight);
      }
    });

    const containerWindow = ownerWindow(scrollbar);

    this.#handleScrollbarSizeChange(scrollbarHeight);
    containerWindow.addEventListener("resize", handleResize);
    return () => {
      handleResize.clear();
      containerWindow.removeEventListener("resize", handleResize);
    };
  }

  #setScrollButtonDisplay(show, type) {
    const scrollButtons = this.#getScrollButtons();
    if (
      !scrollButtons ||
      !Array.isArray(scrollButtons) ||
      scrollButtons.length === 0
    ) {
      return;
    }
    scrollButtons.forEach((scrollButton) => {
      if (scrollButton.dataset.scrollButton === type) {
        scrollButton.classList[show ? "add" : "remove"]("disabled");
      }
    });
  }

  #setScrollButtonDisplayStart(show) {
    return this.#setScrollButtonDisplay(show, "decrement");
  }

  #setScrollButtonDisplayEnd(show) {
    return this.#setScrollButtonDisplay(show, "increment");
  }

  /**
   * Toggle visibility of start and end scroll buttons
   * Using IntersectionObserver on first and last Tabs.
   */
  #scrollButtonDisplayListener() {
    const tabsList = this.#getTabsList();
    const tabsScroller = this.#getTabsScroller();
    if (!tabsList || !tabsScroller) {
      return () => {};
    }
    const tabListChildren = Array.from(tabsList.children);
    const length = tabListChildren.length;

    if (typeof IntersectionObserver !== "undefined" && length > 0) {
      const firstTab = tabListChildren[0];
      const lastTab = tabListChildren[length - 1];
      const observerOptions = {
        root: tabsScroller,
        threshold: 0.99,
      };

      const handleScrollButtonStart = (entries) => {
        const show = entries[0].isIntersecting;
        this.#setScrollButtonDisplayStart(show);
      };
      const firstObserver = new IntersectionObserver(
        handleScrollButtonStart,
        observerOptions
      );
      firstObserver.observe(firstTab);

      const handleScrollButtonEnd = (entries) => {
        const show = entries[0].isIntersecting;
        this.#setScrollButtonDisplayEnd(show);
      };
      const lastObserver = new IntersectionObserver(
        handleScrollButtonEnd,
        observerOptions
      );
      lastObserver.observe(lastTab);

      return () => {
        firstObserver.disconnect();
        lastObserver.disconnect();
      };
    }

    return () => {};
  }

  register(element) {
    if (
      typeof element === "undefined" ||
      typeof element !== "object" ||
      element === null ||
      element.nodeType !== Node.ELEMENT_NODE
    ) {
      throw new Error(
        '[MarkScrollableTabs]: Expected element to be an instance of HTMLElement but received "' +
          typeof element +
          '"'
      );
    }

    this.#element = element;
    this.#cleanup.push(() => {
      this.#element = null;
    });
    const scrollButtons = this.#getScrollButtons();
    const scrollButtonsCount = scrollButtons.length;
    let i = 0;
    while (i < scrollButtonsCount) {
      const scrollButton = scrollButtons[i];
      i++;
      const clickHandler = (
        scrollButton.dataset.scrollButton === "increment"
          ? this.#handleEndScrollClick
          : this.#handleStartScrollClick
      ).bind(this);
      scrollButton.addEventListener("click", clickHandler);
      this.#cleanup.push(() =>
        scrollButton.removeEventListener("click", clickHandler)
      );

      // const doc = ownerWindow(scrollButton);
      // doc.addEventListener("resize");
    }
    this.#cleanup.push(this.#scrollbarSizeListener());
    this.#cleanup.push(this.#scrollButtonDisplayListener());

    return this;
  }

  unregister() {
    if (Array.isArray(this.#cleanup) && this.#cleanup.length > 0) {
      this.#cleanup.forEach((cleanup) => {
        if (typeof cleanup === "function") {
          this.#cleanup();
        }
      });
    }
    this.#cleanup = [];

    return this;
  }

  #getTabsMeta() {
    const tabsScroller = this.#getTabsScroller();
    const tabsList = this.#getTabsList();
    let tabsMeta;
    let tabMeta;

    if (tabsScroller && tabsList) {
      const rect = tabsScroller.getBoundingClientRect();
      // create a new object with ClientRect class props + scrollLeft
      tabsMeta = {
        clientWidth: tabsScroller.clientWidth,
        scrollLeft: tabsScroller.scrollLeft,
        scrollTop: tabsScroller.scrollTop,
        scrollWidth: tabsScroller.scrollWidth,
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      };

      if (tabsList.children && tabsList.children.length > 0) {
        const tab = tabsList.querySelector(":scope>.active");
        tabMeta = tab ? tab.getBoundingClientRect() : null;
      }
    }

    return { tabsMeta, tabMeta };
  }

  scrollSelectedIntoView(animation) {
    if (!this.#element) {
      return;
    }
    const { tabsMeta, tabMeta } = this.#getTabsMeta();

    if (!tabMeta || !tabsMeta) {
      return;
    }

    const start = 'left'
    const end = 'right'
    const scrollStart = 'scrollLeft';
    if (tabMeta[start] < tabsMeta[start]) {
      // left side of button is out of view
      const nextScrollStart =
        tabsMeta[scrollStart] + (tabMeta[start] - tabsMeta[start]);
      this.#scroll(nextScrollStart + tabMeta.width, { animation });
    } else if (tabMeta[end] > tabsMeta[end]) {
      // right side of button is out of view
      const nextScrollStart =
        tabsMeta[scrollStart] + (tabMeta[end] - tabsMeta[end]);
      this.#scroll(nextScrollStart + tabMeta.width, { animation });
    }
  }
}
